// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useCallback, useMemo } from 'react';

import { type Manufacturer, useEvolvePost } from '@evolvemep/api-ts';

import { useGeneralContext } from 'helpers/useGeneralContext';
import { useEvolveGetWithLocalStorage } from 'hooks/useEvolveGetWithLocalStorage';

type ManufacturersContextType = {
  manufacturers: Manufacturer[] | undefined;
  addManufacturer: (manufacturerName: string) => Promise<Manufacturer>;
  loading: boolean;
  mutationRunning: boolean;
};

const ManufacturersContext = createContext<ManufacturersContextType | undefined>(undefined);

export const ManufacturersProvider = ({ children }: { children: ReactNode }) => {
  const { data: manufacturers, loading, updateStoredValue } = useEvolveGetWithLocalStorage('shop/partCatalog/mfg');
  const { apiCall: addManufacturerApiCall, loading: mutationRunning } = useEvolvePost('shop/partCatalog/mfg');

  const addManufacturer = useCallback<ManufacturersContextType['addManufacturer']>(
    async (name) =>
      addManufacturerApiCall({
        name,
      }).then((m) => {
        updateStoredValue((v = []) => [...v, m].toSorted((a, b) => a.name.localeCompare(b.name)));
        return m;
      }),
    [addManufacturerApiCall, updateStoredValue],
  );

  const value = useMemo<ManufacturersContextType>(
    () => ({
      manufacturers,
      addManufacturer,
      loading,
      mutationRunning,
    }),
    [addManufacturer, loading, manufacturers, mutationRunning],
  );
  return <ManufacturersContext value={value}>{children}</ManufacturersContext>;
};

export const useManufacturers = () => useGeneralContext(ManufacturersContext, 'Manufacturers');
